import { Box, Chip, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import { Tooltip } from "../../molecules";
import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";
import { checkColorContrast } from "../utils";

export interface PMSmallTableItem {
  /** (string optional) The label to display on the left. */
  label?: string;
  /** (string) The text to display inside the chip. */
  value: string;
  /** (string) Background color of the chip (any CSS color or theme key). */
  chipColor: string;
  /** (string) Text color of the chip (any CSS color or theme key). */
  chipTextColor: string;
  /** (number optional) A number indicating after how many characters the value should be shortened and a mousover displayed */
  cutValueAfter?: number;
  /** (boolean optional) Allows the chip value to wrap onto at most two lines. */
  wrapValue?: boolean;
}

export interface PMSmallTableProps extends Omit<PMBaseCardProps, "children"> {
  /** Up to two items to display; extras are ignored. */
  items: PMSmallTableItem[];
}

/**
 * PMSmallTable
 * A small table component that displays up to two rows of data in a card format.
 */
export const PMSmallTable: PimoReactComponent<PMSmallTableProps> = ({
  items,
  icon = "InfoOutlined",
  title,
  showInfoIcon = false,
  showMenuIcon = false,
  ...restCardProps
}) => {
  const theme = useTheme();
  const rows = items.slice(0, 2);

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      {...restCardProps}
    >
      <Box
        sx={{
          height: "100%",
          display: "grid",
          gridTemplateRows:
            rows.length === 1 && rows[0].wrapValue ? "1fr" : "repeat(2, 1fr)",
          alignItems: "center",
          justifyItems: "stretch",
          pl: 0.3,
          pr: 0.3,
          pt: 0,
          gap: 1,
        }}
      >
        {rows.map((row, idx) => {
          const cutValue =
            row.cutValueAfter != null && row.cutValueAfter > 0
              ? row.cutValueAfter < row.value.length
              : false;

          const contrastTextColor = checkColorContrast({
            backgroundColor: row.chipColor,
            textColor: row.chipTextColor,
          })?.textColor;

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  textAlign: "left",
                  width: "auto",
                  lineHeight: 1.25,
                }}
              >
                {row.label}
              </Typography>
              <Tooltip
                title={cutValue || row.wrapValue ? row.value : undefined}
              >
                <Chip
                  label={
                    cutValue && !row.wrapValue
                      ? row.value.substring(0, row.cutValueAfter) + "..."
                      : row.value
                  }
                  size="small"
                  sx={{
                    backgroundColor: row.chipColor,
                    color: contrastTextColor,
                    fontWeight: 500,
                    px: 0.8,
                    py: 1,
                    ...(row.wrapValue && {
                      maxWidth: "100%",
                      minWidth: 0,
                      height: "auto",
                      "& .MuiChip-label": {
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        overflow: "hidden",
                      },
                    }),
                  }}
                />
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </PMBaseCard>
  );
};

export default PMSmallTable;
